import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LogOut,
  ChevronDown,
  Eye,
  X,
  Moon,
  Sun,
  PhoneIncoming,
  Rows3,
  Check,
  Wifi,
  WifiOff,
} from 'lucide-react'
import { useUser } from '@/contexts/UserContext'
import { getNavGroupDestinations } from '@/config/navigationConfig'
import { ImpersonateUserModal } from '@/components/ImpersonateUserModal'
import { apiClient } from '@/lib/api'
import { socketClient } from '@/lib/socket'
import { getUserTypeDisplayName } from '@/lib/userTypeDisplay'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { useVersion } from '@/hooks/use-version'
import { useDensity, type DensityMode } from '@/hooks/use-density'
import { cn } from '@/lib/utils'
import { GRACKLE_COLORS } from '@/lib/theme'

interface UserMenuProps {
  darkMode: boolean
  onToggleDarkMode: () => void
  isConnected: boolean
  onLogIncomingCall: () => void
}

export function UserMenu({
  darkMode,
  onToggleDarkMode,
  isConnected,
  onLogIncomingCall,
}: UserMenuProps) {
  const {
    currentUser,
    userType,
    effectiveUserType,
    impersonatedUser,
    isImpersonating,
    endImpersonation,
    isAdmin: hasAdminAccess,
  } = useUser()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { data: versionInfo } = useVersion()
  const { density, setDensity } = useDensity()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [impersonateModalOpen, setImpersonateModalOpen] = useState(false)

  const isAdmin = currentUser?.user_type === 'Admin'
  const adminItems = getNavGroupDestinations('admin', effectiveUserType)

  const handleExitImpersonation = () => {
    endImpersonation()
    toast({
      title: 'Impersonation ended',
      description: 'You are now viewing as yourself.',
    })
    window.location.reload()
  }

  const handleLogout = async () => {
    setIsLoggingOut(true)

    try {
      // Disconnect socket before logout to prevent memory leaks
      socketClient.disconnect()

      await apiClient.logout()

      // Toast notification (may not be visible if redirect is immediate)
      toast({
        title: 'Logged out successfully',
        description: 'You have been signed out of your account.',
      })
    } catch (error) {
      console.error('Logout error:', error)

      // Even if there's an error, logout() will still redirect
      // This catch block is just for logging
    }
  }

  if (!currentUser) {
    return null
  }

  const userInitials = (currentUser.name || '?')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // Extract email from Google OAuth user object or use the email field directly
  const userEmail = currentUser.email || 'No email'

  // Get Google photo from OAuth user object if available
  const googlePhotoUrl = (currentUser as any).picture || null

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex items-center gap-2 hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="User menu"
            aria-haspopup="true"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={googlePhotoUrl} alt={`${currentUser.name} profile picture`} />
              <AvatarFallback
                className="text-xs font-medium"
                style={{
                  backgroundColor: GRACKLE_COLORS.paleGrey,
                  color: GRACKLE_COLORS.navy,
                }}
              >
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <ChevronDown
              className="h-4 w-4"
              style={{ color: GRACKLE_COLORS.navy }}
              aria-hidden="true"
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64" aria-label="User menu options">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-2">
              <div className="flex items-center gap-2">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={googlePhotoUrl} alt={`${currentUser.name} profile picture`} />
                  <AvatarFallback
                    className="font-medium"
                    style={{
                      backgroundColor: GRACKLE_COLORS.paleGrey,
                      color: GRACKLE_COLORS.navy,
                    }}
                  >
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <p className="text-sm font-medium leading-none">{currentUser.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{userEmail}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={userType === 'Admin' ? 'default' : 'secondary'} className="text-xs">
                  {getUserTypeDisplayName(userType)}
                </Badge>
              </div>
            </div>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          {/* Impersonation controls for Admin users */}
          {isAdmin && (
            <>
              {isImpersonating && impersonatedUser ? (
                <>
                  <div
                    className="px-2 py-2 border-y"
                    style={{
                      backgroundColor: GRACKLE_COLORS.pollen,
                      borderColor: GRACKLE_COLORS.pollen,
                    }}
                  >
                    <div
                      className="flex items-center gap-2"
                      style={{ color: GRACKLE_COLORS.prussianBlue }}
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium">Viewing as</p>
                        <p className="text-sm font-semibold truncate">{impersonatedUser.name}</p>
                      </div>
                    </div>
                  </div>
                  <DropdownMenuItem
                    onClick={handleExitImpersonation}
                    className="cursor-pointer hover:opacity-70"
                    style={{ color: GRACKLE_COLORS.prussianBlue }}
                    aria-label="Exit impersonation mode"
                  >
                    <X className="mr-2 h-4 w-4" aria-hidden="true" />
                    <span>Exit Impersonation</span>
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem
                  onClick={() => setImpersonateModalOpen(true)}
                  className="cursor-pointer hover:opacity-70"
                  aria-label="View CRM as another user"
                >
                  <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
                  <span>View as User...</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Admin / app navigation (moved out of the sidebar) */}
          {adminItems.length > 0 && (
            <>
              {hasAdminAccess && (
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  Admin
                </DropdownMenuLabel>
              )}
              {adminItems.map((item) => {
                const Icon = item.icon
                return (
                  <DropdownMenuItem
                    key={item.href}
                    onClick={() => navigate(item.href)}
                    className="cursor-pointer"
                    aria-label={item.label}
                  >
                    <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
                    <span>{item.label}</span>
                  </DropdownMenuItem>
                )
              })}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Settings moved from header */}
          <DropdownMenuItem
            onClick={onToggleDarkMode}
            className="cursor-pointer"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? (
              <Sun className="mr-2 h-4 w-4" aria-hidden="true" />
            ) : (
              <Moon className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </DropdownMenuItem>

          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="cursor-pointer">
              <Rows3 className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>
                Density: <span className="capitalize">{density}</span>
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {(
                [
                  ['comfortable', 'Comfortable'],
                  ['default', 'Default'],
                  ['compact', 'Compact'],
                ] as const
              ).map(([value, label]) => (
                <DropdownMenuItem
                  key={value}
                  onClick={() => setDensity(value as DensityMode)}
                  className="cursor-pointer"
                  aria-current={density === value ? 'true' : undefined}
                >
                  <span className="flex-1">{label}</span>
                  {density === value && (
                    <Check className="ml-2 h-4 w-4 text-primary" aria-hidden="true" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuItem
            onClick={onLogIncomingCall}
            className="cursor-pointer"
            aria-label="Log incoming call"
          >
            <PhoneIncoming className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>Log Incoming Call</span>
          </DropdownMenuItem>

          <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
            {isConnected ? (
              <Wifi className="h-4 w-4 text-success" aria-hidden="true" />
            ) : (
              <WifiOff className="h-4 w-4 text-destructive" aria-hidden="true" />
            )}
            <span className={cn(isConnected ? 'text-success' : 'text-destructive')}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="cursor-pointer hover:opacity-70"
            style={{ color: GRACKLE_COLORS.darkRaspberry }}
            aria-label="Log out of your account"
          >
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>{isLoggingOut ? 'Logging out...' : 'Log out'}</span>
          </DropdownMenuItem>

          {versionInfo?.version && (
            <>
              <DropdownMenuSeparator />
              <div
                className="px-2 py-1.5 text-xs text-muted-foreground text-center"
                aria-label={`Application version ${versionInfo.version}`}
              >
                v{versionInfo.version}
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Impersonation Modal */}
      {isAdmin && (
        <ImpersonateUserModal open={impersonateModalOpen} onOpenChange={setImpersonateModalOpen} />
      )}
    </>
  )
}
